/**
 * One-shot handoff for prefilling the Proposals calculator from another feature
 * (e.g. "Create Proposal" on a contract).
 *
 * The payload mirrors the calculator's in-memory draft ({@link Header} +
 * `LineRow[]` + `DiscountRow[]`). It's stashed in sessionStorage (not the
 * persistent localStorage draft) so it never clobbers a saved-draft in progress,
 * and it's read exactly once — {@link readPrefill} removes it on read. The
 * `Header` type lives here (rather than in ProposalsPage) so both the producer
 * and the calculator share one definition without a circular import.
 */
import type { RenewalTerm } from "./api";
import type { BundleRow, DiscountRow, LineRow } from "./calc";

export interface Header {
  customer_name: string;
  proposal_name: string;
  contract_months: number;
  closer_id: string;
  locations: number | null;
  is_auto_renew: boolean;
  renewal_term: RenewalTerm;
  renewal_term_months: number | null;
  mtm_uplift_pct: number | null;
  billing_effective_from: string | null; // ISO YYYY-MM-DD
  notes: string;
}

export interface ProposalPrefill {
  header: Header;
  bundles?: BundleRow[];
  lineItems: LineRow[];
  discountRows: DiscountRow[];
}

export const PREFILL_KEY = "cinch-ops-proposal-prefill-v1";

/** Stash a prefill for the next mount of the calculator. */
export function writePrefill(prefill: ProposalPrefill): void {
  try {
    sessionStorage.setItem(PREFILL_KEY, JSON.stringify(prefill));
  } catch {
    /* sessionStorage unavailable — the calculator just opens empty */
  }
}

/** Read and consume a pending prefill (removed on read). Returns null if none. */
export function readPrefill(): ProposalPrefill | null {
  try {
    const raw = sessionStorage.getItem(PREFILL_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(PREFILL_KEY);
    const parsed = JSON.parse(raw) as ProposalPrefill;
    if (!parsed || !parsed.header || !Array.isArray(parsed.lineItems)) return null;
    return parsed;
  } catch {
    try {
      sessionStorage.removeItem(PREFILL_KEY);
    } catch {
      /* ignore */
    }
    return null;
  }
}
